import { createSlice } from '@reduxjs/toolkit';
import {
  handleSetSelectedFilterIds,
  handleSetSelectedContentBasedValues,
  handleSetSelectedFilterValue,
  handleSetSelectedCategories,
  handleResetFilter,
  handleSetSelectedItems,
  handleSetSelectedSentimentValues,
} from 'store/helpers/filterHelpers';
import { xor } from 'lib';
import { alarmInitialState } from './initialState';
import {
  addAlarmChannel,
  createAlarm,
  editAlarm,
  fetchApps,
  fetchOtherApps,
  getAlarm,
  getChannels,
} from './actions';

export const alarmSlice = createSlice({
  name: 'alarmSlice',
  initialState: alarmInitialState,
  reducers: {
    setAlarmParamValue: (state, action) => {
      const { key, value } = action.payload;
      state[key] = value;
    },
    toggleParamInArray: (state, action) => {
      const {
        key,
        subKey = null,
        value,
        isInitialState = false,
      } = action.payload;

      if (isInitialState) {
        state[key] = value;
      } else if (subKey) state[key][subKey] = xor(state[key][subKey], [value]);
      else state[key] = xor(state[key], [value]);
    },
    changeAlarmTypeFilterStatus: (state, action) => {
      const { type, platform, filter } = action.payload;
      state.type[type].filters[platform][filter].check =
        !state.type[type].filters[platform][filter].check;
    },
    setSelectedFilterIds: (state, action) => {
      handleSetSelectedFilterIds(state, action);
    },
    setSelectedContentBasedValues: (state, action) => {
      handleSetSelectedContentBasedValues(state, action);
    },
    setSelectedFilterValue: (state, action) => {
      handleSetSelectedFilterValue(state, action, alarmInitialState.filters);
    },
    resetAllFilters: (state) => {
      state.filters = alarmInitialState.filters;
    },
    toggleAlarmFilterActivateSwitch: (state, action) => {
      const { componentName } = action.payload;
      const filter = state.filters[state.selectedPreviewTab][componentName];
      filter.isDisabled = !filter.isDisabled;
    },
    setSelectedCategories: (state, action) => {
      handleSetSelectedCategories(state, action);
    },
    resetFilter: (state, action) => {
      handleResetFilter(state, action, alarmInitialState.filters);
    },
    setSelectedItems: (state, action) => {
      handleSetSelectedItems(state, action);
    },
    setSelectedSentimentValues: (state, action) => {
      handleSetSelectedSentimentValues(state, action);
    },
    resetAlarmState: () => alarmInitialState,
    setResourceFiltersIncludeExclude: (state, action) => {
      state.selectedPlatforms.map((p) => {
        if (state.filters[p].resourcesFilter)
          state.filters[p].resourcesFilter.hasIncludeExclude = action.payload;
        if (state.filters[p].resourceCategoryFilter)
          state.filters[p].resourceCategoryFilter.hasIncludeExclude =
            action.payload;
      });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createAlarm.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(createAlarm.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAlarm.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(editAlarm.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(editAlarm.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editAlarm.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getAlarm.fulfilled, (_, action) => {
        return { ...action?.payload };
      })
      .addCase(getAlarm.pending, (state) => {
        state.getAlarmRequest.loading = true;
        state.getAlarmRequest.error = null;
      })
      .addCase(getAlarm.rejected, (state, action) => {
        state.getAlarmRequest.loading = false;
        state.getAlarmRequest.error = action.payload;
      })
      .addCase(getChannels.fulfilled, (state, action) => {
        state.channels.data = action.payload;
        state.channels.loading = false;
        state.channels.error = null;
      })
      .addCase(getChannels.pending, (state) => {
        state.channels.loading = true;
        state.channels.error = null;
      })
      .addCase(getChannels.rejected, (state, action) => {
        state.channels.loading = false;
        state.channels.error = action.payload;
      })
      .addCase(addAlarmChannel.fulfilled, (state, action) => {
        state.channels.data.push(action.payload);
        state.selectedChannelId = action.payload.id;
        state.addChannel.loading = false;
        state.addChannel.error = null;
      })
      .addCase(addAlarmChannel.pending, (state) => {
        state.addChannel.loading = true;
        state.addChannel.error = null;
      })
      .addCase(addAlarmChannel.rejected, (state, action) => {
        state.addChannel.loading = false;
        state.addChannel.error = action.payload;
      })
      .addCase(fetchApps.fulfilled, (state, action) => {
        state.apps.data = action.payload;
        state.apps.loading = false;
        state.apps.error = null;
      })
      .addCase(fetchApps.pending, (state) => {
        state.apps.loading = true;
        state.apps.error = null;
        state.apps.data = [];
      })
      .addCase(fetchApps.rejected, (state, action) => {
        state.apps.loading = false;
        state.apps.error = action.payload;
        state.apps.data = [];
      })
      .addCase(fetchOtherApps.fulfilled, (state, action) => {
        const incoming = action.payload || [];
        const selectedIds = new Set(state.selectedAppIds || []);
        const incomingIds = new Set(incoming.map((app) => app.id));

        /**
         * آیتم‌های انتخاب‌شده‌ای که در نتیجه‌ی جدید نیستند نگه داشته می‌شوند تا
         * لیبل بج‌هایشان از بین نرود، ولی ترتیب نتیجه‌ی سرور دست‌نخورده می‌ماند
         * تا آیتم‌ها با انتخاب‌شدن جای خود را در لیست عوض نکنند
         */
        const keptSelected = (state.otherApps.data || []).filter(
          (app) => selectedIds.has(app.id) && !incomingIds.has(app.id)
        );

        state.otherApps.data = [...incoming, ...keptSelected];
        state.otherApps.loading = false;
        state.otherApps.error = null;
      })
      .addCase(fetchOtherApps.pending, (state) => {
        state.otherApps.loading = true;
        state.otherApps.error = null;
      })
      .addCase(fetchOtherApps.rejected, (state, action) => {
        state.otherApps.loading = false;
        state.otherApps.error = action.payload;
      });
  },
});

export const {
  setAlarmParamValue,
  changeAlarmTypeFilterStatus,
  setSelectedFilterIds,
  setSelectedContentBasedValues,
  setSelectedFilterValue,
  resetAllFilters,
  toggleAlarmFilterActivateSwitch,
  setSelectedCategories,
  resetFilter,
  setSelectedItems,
  setSelectedSentimentValues,
  resetAlarmState,
  toggleParamInArray,
  setResourceFiltersIncludeExclude,
} = alarmSlice.actions;
